import { Zap, Wrench, AlignHorizontalSpaceBetween, RectangleHorizontal, ArrowUpRight, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const NAVY_DEEP = "#040A1F";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Service = {
  num: string;
  tag: string;
  title: string;
  description: string;
  meta: string[];
  href: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
};
const SERVICES: Service[] = [{
  num: "01",
  tag: "Torsion & Extension",
  title: "Spring Replacement",
  description: "The most common emergency call we get. Snapped springs replaced with matched, warranted units — usually inside 90 minutes.",
  meta: ["~90 min", "All sizes", "12-mo warranty"],
  href: "/garage-door-spring-replacement-sydney/",
  icon: Wrench
}, {
  num: "02",
  tag: "Openers & Automation",
  title: "Motor & Opener Repair",
  description: "B&D, Merlin, Chamberlain, ATA — diagnosed and fixed on-site, or replaced like-for-like if the motor's beyond economic repair.",
  meta: ["1–2 hrs", "All brands", "Same-day"],
  href: "/garage-door-motor-repair-sydney/",
  icon: Zap
}, {
  num: "03",
  tag: "Tension Systems",
  title: "Cable Replacement",
  description: "Frayed, snapped, or off-drum cables put your door — and family — at real risk. Replaced with new wire and re-tensioned to spec.",
  meta: ["~60 min", "Precision work", "Workmanship warranty"],
  href: "/garage-door-cable-repair-sydney/",
  icon: AlignHorizontalSpaceBetween
}, {
  num: "04",
  tag: "Roller & Tilt",
  title: "Roller Door Service",
  description: "Stuck, noisy, or jammed roller doors — diagnosed, lubricated, parts replaced, or fully re-balanced. Single-cycle through to industrial.",
  meta: ["Same-day", "Residential + Strata", "Sectional + Roller"],
  href: "/roller-door-repair-sydney/",
  icon: RectangleHorizontal
}];
export default function RelatedServices() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY
  }} aria-labelledby="related-services-heading">
      {/* Background gradient */}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: `radial-gradient(ellipse at 85% 20%, ${NAVY_LIGHT}80 0%, transparent 50%), radial-gradient(ellipse at 15% 90%, ${NAVY_DEEP} 0%, transparent 60%)`
    }} />

      {/* Subtle grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.9 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* === Top strip === */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-14 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD}66`,
            backgroundColor: `${GOLD}14`
          }}>
              <Layers className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD
          }}>
              Related Repair Services
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${GOLD}40`
          }} />
          </div>

          <Link href="/garage-door-services-sydney/" className="group inline-flex items-center gap-2 text-[12px] font-bold tracking-[0.14em] uppercase transition-colors" style={{
          color: "rgba(255,255,255,0.7)",
          fontFamily: FONT_BODY
        }} onMouseEnter={e => e.currentTarget.style.color = GOLD} onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.7)"}>
            View all services
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
          </Link>
        </div>

        {/* === Headline === */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
          <h2 id="related-services-heading" className="lg:col-span-8 font-extrabold text-white" style={{
          fontFamily: FONT_HEAD,
          fontSize: "clamp(2rem, 5vw, 4rem)",
          lineHeight: 1.02,
          letterSpacing: "-0.03em"
        }}>
            One number,{" "}
            <span style={{
            color: GOLD
          }}>every repair</span>
            <br className="hidden md:block" />
            your garage door needs.
          </h2>
          <p className="lg:col-span-4 text-[15px] leading-relaxed" style={{
          color: "rgba(255,255,255,0.62)"
        }}>
            Whatever's failed &mdash; spring, motor, cable, roller, panel
            &mdash; the same crew, the same fixed-price guarantee, the same
            same-day reach.
          </p>
        </div>

        {/* === 4-card grid === */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {SERVICES.map(service => <ServiceCard key={service.num} service={service} />)}
        </div>

        {/* === Bottom "view all" strip === */}
        <div className="mt-14 md:mt-16">
          <Link href="/garage-door-services-sydney/" className="group flex items-center justify-between gap-4 rounded-2xl border px-6 md:px-8 py-5 md:py-6 transition-all duration-300" style={{
          borderColor: "rgba(255,255,255,0.10)",
          backgroundColor: "rgba(255,255,255,0.02)"
        }} onMouseEnter={e => {
          e.currentTarget.style.borderColor = `${GOLD}66`;
          e.currentTarget.style.backgroundColor = `${GOLD}0A`;
        }} onMouseLeave={e => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
          e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.02)";
        }}>
            <div className="flex items-center gap-4 md:gap-5">
              <span className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl border" style={{
              borderColor: `${GOLD}40`,
              backgroundColor: `${GOLD}1A`
            }}>
                <Layers className="h-5 w-5" strokeWidth={2} style={{
                color: GOLD
              } as React.CSSProperties} />
              </span>
              <div>
                <div className="text-[10px] font-bold tracking-[0.24em] uppercase mb-1" style={{
                color: GOLD
              }}>
                  Browse the full menu
                </div>
                <div className="font-bold text-white text-[15px] md:text-base" style={{
                fontFamily: FONT_HEAD,
                letterSpacing: "-0.01em"
              }}>
                  Installation, service plans, brands & door types &mdash; all in one place.
                </div>
              </div>
            </div>
            <ArrowUpRight className="flex-shrink-0 h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2} style={{
            color: GOLD
          } as React.CSSProperties} />
          </Link>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent 0%, ${GOLD}66 50%, transparent 100%)`
    }} />
    </section>;
}
function ServiceCard({
  service
}: {
  service: Service;
}) {
  const Icon = service.icon;
  return <Link href={service.href} className="group relative block rounded-2xl border overflow-hidden transition-all duration-400" style={{
    backgroundColor: "rgba(255,255,255,0.025)",
    borderColor: "rgba(255,255,255,0.08)"
  }} onMouseEnter={e => {
    e.currentTarget.style.borderColor = `${GOLD}66`;
    e.currentTarget.style.backgroundColor = "rgba(245,166,35,0.04)";
    e.currentTarget.style.transform = "translateY(-4px)";
    e.currentTarget.style.boxShadow = `0 24px 60px -20px ${NAVY_DEEP}, 0 0 0 1px ${GOLD}26`;
  }} onMouseLeave={e => {
    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.025)";
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "none";
  }}>
      {/* Top-right corner notch */}
      <div aria-hidden className="absolute top-0 right-0 w-10 h-10 pointer-events-none opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
      background: `linear-gradient(225deg, ${GOLD}40 0%, transparent 60%)`
    }} />

      {/* Watermark number */}
      <div aria-hidden className="absolute -top-2 -right-1 font-extrabold leading-none select-none pointer-events-none transition-all duration-500 group-hover:opacity-30" style={{
      fontFamily: FONT_HEAD,
      color: `${GOLD}1A`,
      fontSize: "clamp(5rem, 11vw, 8rem)",
      letterSpacing: "-0.05em",
      fontVariantNumeric: "tabular-nums",
      opacity: 0.7
    }}>
        {service.num}
      </div>

      <div className="relative p-6 md:p-7 flex flex-col h-full min-h-[340px]">
        {/* Top: icon + tag */}
        <div className="flex items-center gap-2.5 mb-6">
          <span className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-all duration-300" style={{
          borderColor: `${GOLD}40`,
          backgroundColor: `${GOLD}14`
        }}>
            <Icon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" strokeWidth={2} style={{
            color: GOLD
          } as React.CSSProperties} />
          </span>
          <span className="text-[10px] font-bold tracking-[0.18em] uppercase" style={{
          color: GOLD
        }}>
            {service.tag}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-extrabold text-white mb-3 transition-colors duration-200 group-hover:text-[#F5A623]" style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(1.125rem, 1.6vw, 1.375rem)",
        lineHeight: 1.15,
        letterSpacing: "-0.018em"
      }}>
          {service.title}
        </h3>

        {/* Description */}
        <p className="text-[13.5px] leading-[1.6] mb-6 flex-grow" style={{
        color: "rgba(255,255,255,0.65)"
      }}>
          {service.description}
        </p>

        {/* Meta divider */}
        <div aria-hidden className="h-px mb-4" style={{
        background: `linear-gradient(90deg, ${GOLD}40 0%, transparent 100%)`
      }} />

        {/* Meta + arrow row */}
        <div className="flex items-end justify-between gap-3">
          <ul className="flex flex-col gap-1.5">
            {service.meta.map(m => <li key={m} className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.1em] uppercase" style={{
            color: "rgba(255,255,255,0.5)"
          }}>
                <span className="inline-block w-1 h-1 rounded-full" style={{
              backgroundColor: GOLD
            }} />
                {m}
              </li>)}
          </ul>

          <div className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-300 group-hover:scale-110" style={{
          borderColor: `${GOLD}66`,
          backgroundColor: `${GOLD}14`
        }}>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} style={{
            color: GOLD
          } as React.CSSProperties} />
          </div>
        </div>
      </div>
    </Link>;
}